import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/** Superadmin extraction/RSS queue polling — lifted from the old ProfilePanel. */

export interface ExtractionQueueData {
  queue: {
    pending_total: number;
    pending_new: number;
    pending_legacy: number;
    total_articles: number;
    total_extracted: number;
    percent_complete: number;
  };
  scheduler: {
    is_running: boolean;
    paused: boolean;
    interval_seconds: number;
    batch_size: number;
    transcription_enabled?: boolean;
  };
  transcription?: {
    pending_av: number;
    total_av: number;
    av_transcribed: number;
    av_failed: number;
  };
  stats: {
    total_processed: number;
    total_enriched: number;
    total_failed: number;
    total_batches: number;
    last_run_at: string | null;
    avg_seconds_per_item: number | null;
  };
  estimate: {
    items_remaining: number;
    batches_remaining: number;
    estimated_minutes: number | null;
  };
}

export interface RssStatus {
  paused: boolean;
  running: boolean;
}

export interface PublisherStatusData {
  scheduler: {
    is_running: boolean;
    enabled: boolean;
    paused: boolean;
    timezone: string;
    planner_interval_minutes: number;
    dispatcher_interval_seconds: number;
    last_planner_run_at: string | null;
    last_dispatcher_run_at: string | null;
    consecutive_failures: number;
  };
  queue: {
    total_tickets: number;
    queued: number;
    scheduled: number;
    publishing: number;
    failed: number;
    published_today: number;
    active_lines: number;
  };
  next: {
    scheduled_at: string | null;
    brand_slug: string | null;
    platform: string;
    tend_content_id: number;
  } | null;
  lines: Array<{
    brand_slug: string;
    platform: string;
    queued: number;
    scheduled: number;
    publishing: number;
    failed: number;
    next_scheduled_at: string | null;
  }>;
}

const HEADERS = { headers: { 'X-API-Key': 'dev-api-key-2024' } };

export const useAdminQueues = (active: boolean) => {
  const [extractionQueue, setExtractionQueue] = useState<ExtractionQueueData | null>(null);
  const [rssStatus, setRssStatus] = useState<RssStatus | null>(null);
  const [publisherStatus, setPublisherStatus] = useState<PublisherStatusData | null>(null);

  const fetchExtractionQueue = useCallback(async () => {
    try {
      const resp = await axios.get('/api/content/extraction/queue', HEADERS);
      setExtractionQueue(resp.data);
    } catch {
      // Silently fail — admin-only convenience feature
    }
  }, []);

  const fetchRssStatus = useCallback(async () => {
    try {
      const resp = await axios.get('/api/content/rss/status', HEADERS);
      setRssStatus(resp.data);
    } catch {
      // Silently fail
    }
  }, []);

  const toggleExtractionPause = useCallback(async () => {
    const endpoint = extractionQueue?.scheduler?.paused
      ? '/api/content/extraction/resume'
      : '/api/content/extraction/pause';
    try {
      await axios.post(endpoint, null, HEADERS);
      fetchExtractionQueue();
    } catch (err) {
      console.error('Failed to toggle extraction:', err);
    }
  }, [extractionQueue, fetchExtractionQueue]);

  const toggleRssPause = useCallback(async () => {
    const endpoint = rssStatus?.paused
      ? '/api/content/rss/resume'
      : '/api/content/rss/pause';
    try {
      await axios.post(endpoint, null, HEADERS);
      fetchRssStatus();
    } catch (err) {
      console.error('Failed to toggle RSS:', err);
    }
  }, [rssStatus, fetchRssStatus]);

  const fetchPublisherStatus = useCallback(async () => {
    try {
      const resp = await axios.get('/api/tend/publisher/status', HEADERS);
      setPublisherStatus(resp.data);
    } catch {
      // Silently fail
    }
  }, []);

  const togglePublisherPause = useCallback(async () => {
    const endpoint = publisherStatus?.scheduler?.paused
      ? '/api/tend/publisher/resume'
      : '/api/tend/publisher/pause';
    try {
      await axios.post(endpoint, null, HEADERS);
      fetchPublisherStatus();
    } catch (err) {
      console.error('Failed to toggle publisher:', err);
    }
  }, [publisherStatus, fetchPublisherStatus]);

  useEffect(() => {
    if (!active) return;
    fetchExtractionQueue();
    fetchRssStatus();
    fetchPublisherStatus();
    const interval = setInterval(() => {
      fetchExtractionQueue();
      fetchRssStatus();
      fetchPublisherStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [active, fetchExtractionQueue, fetchRssStatus, fetchPublisherStatus]);

  return {
    extractionQueue, rssStatus, publisherStatus,
    toggleExtractionPause, toggleRssPause, togglePublisherPause,
  };
};
